
import { invokeWithRetry } from './common/invokeWithRetry';
import type { DashboardConfig } from '@/store/dashboardConfigStore';

export type ConfigSource = 'current_gym' | 'fallback_gym' | 'default';

export interface DashboardConfigResponse {
  config: DashboardConfig | null;
  source: ConfigSource;
  fallback_gym_id?: string;
}

/**
 * Fetch dashboard config with multi-tenant fallback logic
 * 1. Try to get config for current gym
 * 2. If not found, try to get config from user's other gyms
 * 3. If still not found, return null (will use default)
 */
export async function getDashboardConfig(gymId: string, userId: string): Promise<DashboardConfigResponse> {
  const result = await invokeWithRetry<DashboardConfigResponse>(
    'settings-management',
    { 
      action: 'get_dashboard_config_with_fallback', 
      gym_id: gymId,
      user_id: userId
    }
  );

  if (result.authExpired || result.error) {
    console.error('❌ Error fetching dashboard config:', result.error);
    return { config: null, source: 'default' };
  }

  return result.data || { config: null, source: 'default' };
}

/**
 * Save dashboard config for a specific gym
 */
export async function saveDashboardConfig(gymId: string, config: DashboardConfig): Promise<boolean> {
  const result = await invokeWithRetry<{ setting: any }>(
    'settings-management',
    { 
      action: 'update_dashboard_config', 
      gym_id: gymId, 
      value: config 
    }
  );

  if (result.authExpired || result.error) {
    console.error('❌ Error saving dashboard config:', result.error);
    return false;
  }

  return true;
}
